/*
 * bixdo 全站横向滑动控制器。
 * 维护重点：同页多实例、Theme Editor 重载、键盘方向键、分页和箭头状态必须保持同步。
 */

(() => {
  // 防止同一页面的多个 Section 重复加载资源后重复注册全局事件。
  if (window.BixdoHorizontalRailLoaded) return;
  window.BixdoHorizontalRailLoaded = true;

  const selector = '[data-bixdo-rail]';

  class BixdoHorizontalRail {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-bixdo-rail-track]');
      this.items = Array.from(root.querySelectorAll('[data-bixdo-rail-item]'));
      this.railId = root.dataset.bixdoRailId;
      this.controls = document.querySelector(`[data-bixdo-rail-controls][data-bixdo-rail-target="${this.railId}"]`);
      this.previousButton = this.controls?.querySelector('[data-bixdo-rail-previous]');
      this.nextButton = this.controls?.querySelector('[data-bixdo-rail-next]');
      this.pages = Array.from(this.controls?.querySelectorAll('[data-bixdo-rail-page]') || []);
      this.currentIndex = 0;
      this.rafId = 0;

      if (!this.track || this.items.length < 2) return;

      this.onScroll = this.onScroll.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPrevious = () => this.goTo(this.currentIndex - 1);
      this.onNext = () => this.goTo(this.currentIndex + 1);
      this.onResize = this.onResize.bind(this);

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('keydown', this.onKeydown);
      this.previousButton?.addEventListener('click', this.onPrevious);
      this.nextButton?.addEventListener('click', this.onNext);
      this.pages.forEach((page) => {
        page.addEventListener('click', () => this.goTo(Number(page.dataset.bixdoRailPage)));
      });
      window.addEventListener('resize', this.onResize, { passive: true });
      this.update(0);
    }

    getNearestIndex() {
      const trackStart = this.track.getBoundingClientRect().left;
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      this.items.forEach((item, index) => {
        const distance = Math.abs(item.getBoundingClientRect().left - trackStart);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      return nearestIndex;
    }

    onScroll() {
      window.cancelAnimationFrame(this.rafId);
      this.rafId = window.requestAnimationFrame(() => this.update(this.getNearestIndex()));
    }

    onResize() {
      this.update(this.getNearestIndex());
    }

    onKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      this.goTo(this.currentIndex + direction, 'auto');
    }

    getVisibleItemCount() {
      if (!this.items.length) return 1;
      const itemWidth = this.items[0].getBoundingClientRect().width;
      const styles = window.getComputedStyle(this.track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      return Math.max(1, Math.round((this.track.clientWidth + gap) / (itemWidth + gap)));
    }

    getMaxIndex() {
      return Math.max(0, this.items.length - this.getVisibleItemCount());
    }

    goTo(index, requestedBehavior = 'smooth') {
      const safeIndex = Math.max(0, Math.min(index, this.getMaxIndex()));
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.items[safeIndex].scrollIntoView({
        behavior: reducedMotion ? 'auto' : requestedBehavior,
        block: 'nearest',
        inline: 'start'
      });
      this.update(safeIndex);
    }

    update(index) {
      const maxIndex = this.getMaxIndex();
      this.currentIndex = Math.max(0, Math.min(index, maxIndex));
      this.previousButton?.toggleAttribute('disabled', this.currentIndex === 0);
      this.nextButton?.toggleAttribute('disabled', this.currentIndex === maxIndex);
      this.pages.forEach((page, pageIndex) => {
        const isActive = pageIndex === index;
        page.classList.toggle('is-active', isActive);
        if (isActive) page.setAttribute('aria-current', 'true');
        else page.removeAttribute('aria-current');
      });
    }

    destroy() {
      this.track?.removeEventListener('scroll', this.onScroll);
      this.track?.removeEventListener('keydown', this.onKeydown);
      this.previousButton?.removeEventListener('click', this.onPrevious);
      this.nextButton?.removeEventListener('click', this.onNext);
      window.removeEventListener('resize', this.onResize);
      window.cancelAnimationFrame(this.rafId);
    }
  }

  const initRails = (scope = document) => {
    scope.querySelectorAll(selector).forEach((root) => {
      if (root.bixdoHorizontalRail) return;
      root.bixdoHorizontalRail = new BixdoHorizontalRail(root);
    });
  };

  const destroyRails = (scope) => {
    scope.querySelectorAll(selector).forEach((root) => {
      root.bixdoHorizontalRail?.destroy();
      delete root.bixdoHorizontalRail;
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initRails());
  } else {
    initRails();
  }

  document.addEventListener('shopify:section:load', (event) => initRails(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyRails(event.target));
})();
